import React from "react";
import autoBind from "react-autobind";
import { Button } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import request from "superagent";

const INITIAL_FRAME_TIME_SECONDS = 0.5;
const FRAME_SCAN_STEP_SECONDS = 0.25;
const FRAME_SCAN_MAX_SECONDS = 12;
const MIN_NON_BLACK_AVERAGE = 18;
const MIN_NON_BLACK_PIXEL_RATIO = 0.03;
const MIN_FREE_CROP_SCALE = 0.35;
const MAX_CROP_ZOOM = 4;
const DEFAULT_CROP_ZOOM = 1;
const FREE_CROP_PRESET = "free";
const CROP_PRESET_OPTIONS = [
	{ value: "16:9", label: "16:9", aspectRatio: 16 / 9 },
	{ value: "4:3", label: "4:3", aspectRatio: 4 / 3 },
	{ value: "1:1", label: "1:1", aspectRatio: 1 },
	{ value: "3:4", label: "3:4", aspectRatio: 3 / 4 },
	{ value: "9:16", label: "9:16", aspectRatio: 9 / 16 },
	{ value: FREE_CROP_PRESET, label: "Free", aspectRatio: null }
];

function clampTime(time, duration) {
	if (!Number.isFinite(duration) || duration <= 0) {
		return 0;
	}
	const safeDuration = Math.max(duration - 0.05, 0);
	return Math.min(Math.max(time, 0), safeDuration);
}

function clampValue(value, min, max) {
	return Math.min(Math.max(value, min), max);
}

function normalizePngFileName(fileName = "") {
	const trimmed = `${fileName || ""}`.trim();
	if (!trimmed) {
		return "video-thumbnail.png";
	}
	return trimmed.toLowerCase().endsWith(".png") ? trimmed : `${trimmed}.png`;
}

function getFileNameStemFromValue(value = "") {
	const rawValue = `${value || ""}`.trim();
	if (!rawValue) {
		return "";
	}

	const withoutQuery = rawValue.split("?")[0].split("#")[0];
	const lastSegment = withoutQuery.split("/").pop() || withoutQuery;
	let decodedSegment = lastSegment;
	try {
		decodedSegment = decodeURIComponent(lastSegment);
	} catch (err) {
		decodedSegment = lastSegment;
	}

	return decodedSegment.replace(/\.[^/.]+$/, "");
}

export default class VideoThumbnailModal extends React.PureComponent {
	constructor(props) {
		super(props);
		autoBind(this);
		this.videoRef = React.createRef();
		this.canvasRef = React.createRef();
		this.cropViewportRef = React.createRef();
		this.dragState = null;
		this.openNonce = 0;
		this.previewObjectUrl = null;
		this.state = this.getInitialState(props);
	}

	componentDidMount() {
		window.addEventListener("resize", this.handleWindowResize);
		if (this.props.open) {
			this.resetForOpen(this.props);
		}
	}

	componentDidUpdate(prevProps) {
		if (this.props.open && !prevProps.open) {
			this.resetForOpen(this.props);
			return;
		}

		if (!this.props.open && prevProps.open) {
			this.openNonce += 1;
			this.revokePreviewObjectUrl();
		}

		if (
			this.props.open &&
			(prevProps.videoUrl !== this.props.videoUrl || prevProps.defaultImageName !== this.props.defaultImageName)
		) {
			this.resetForOpen(this.props);
			return;
		}

		if (this.props.open) {
			this.syncCropViewportSize();
		}
	}

	getInitialState(props = this.props) {
		return {
			duration: 0,
			currentTime: 0,
			imageName: this.getDefaultImageName(props),
			autoLinkImage: props.defaultAutoLink !== false,
			videoReady: false,
			renderedVideoHeight: 0,
			capturedImageWidth: 0,
			capturedImageHeight: 0,
			cropViewportWidth: 0,
			cropViewportHeight: 0,
			cropPreset: FREE_CROP_PRESET,
			cropZoom: DEFAULT_CROP_ZOOM,
			cropOffsetX: 0,
			cropOffsetY: 0,
			freeCropWidthPct: 1,
			freeCropHeightPct: 1,
			isDraggingCrop: false,
			seeking: false,
			capturing: false,
			findingFrame: false,
			previewDataUrl: "",
			error: "",
			previewVideoUrl: props.videoUrl || "",
			previewVideoLoading: !!props.videoUrl
		};
	}

	getDefaultImageName(props = this.props) {
		if (props.defaultImageName) {
			return normalizePngFileName(props.defaultImageName);
		}

		const fileStem =
			getFileNameStemFromValue(props.videoFileName) ||
			getFileNameStemFromValue(props.videoUrl) ||
			getFileNameStemFromValue(props.defaultBaseName) ||
			"video";

		return normalizePngFileName(`${fileStem}-thumbnail`);
	}

	resetForOpen(props = this.props) {
		this.openNonce += 1;
		this.revokePreviewObjectUrl();
		this.setState(this.getInitialState(props), () => {
			this.prepareVideoSource(props);
		});
	}

	componentWillUnmount() {
		this.openNonce += 1;
		window.removeEventListener("resize", this.handleWindowResize);
		this.detachCropDragListeners();
		this.revokePreviewObjectUrl();
	}

	revokePreviewObjectUrl() {
		if (this.previewObjectUrl && typeof URL !== "undefined" && typeof URL.revokeObjectURL === "function") {
			URL.revokeObjectURL(this.previewObjectUrl);
		}
		this.previewObjectUrl = null;
	}

	async tryCreateBlobPreviewUrl(videoUrl) {
		if (!videoUrl) {
			return "";
		}

		const response = await request
			.get(videoUrl)
			.withCredentials()
			.responseType("blob");
		const blob = response && response.body ? response.body : null;
		if (!blob || !blob.size) {
			return "";
		}

		if (typeof URL === "undefined" || typeof URL.createObjectURL !== "function") {
			return "";
		}

		return URL.createObjectURL(blob);
	}

	async prepareVideoSource(props = this.props) {
		const directUrl = props.videoUrl || "";
		const nonce = this.openNonce;

		if (!directUrl) {
			this.setState({
				previewVideoUrl: "",
				previewVideoLoading: false,
				error: "No video URL was available for thumbnail generation."
			});
			return;
		}

		this.setState({
			previewVideoUrl: directUrl,
			previewVideoLoading: true,
			error: ""
		});

		try {
			const blobUrl = await this.tryCreateBlobPreviewUrl(directUrl);
			if (nonce !== this.openNonce || !blobUrl) {
				return;
			}
			this.previewObjectUrl = blobUrl;
			this.setState({
				previewVideoUrl: blobUrl,
				previewVideoLoading: false
			});
		} catch (err) {
			console.warn("Unable to preload thumbnail video as blob, falling back to direct URL.", err);
			if (nonce === this.openNonce) {
				this.setState({
					previewVideoUrl: directUrl,
					previewVideoLoading: false
				});
			}
		}
	}

	getVideoElement() {
		return this.videoRef && this.videoRef.current ? this.videoRef.current : null;
	}

	getCanvasElement() {
		return this.canvasRef && this.canvasRef.current ? this.canvasRef.current : null;
	}

	getCropViewportElement() {
		return this.cropViewportRef && this.cropViewportRef.current ? this.cropViewportRef.current : null;
	}

	syncRenderedVideoHeight() {
		const video = this.getVideoElement();
		if (!video) {
			return;
		}

		const nextHeight = Math.round(video.getBoundingClientRect().height || video.clientHeight || 0);
		if (!nextHeight) {
			return;
		}

		if (nextHeight !== this.state.renderedVideoHeight) {
			this.setState({
				renderedVideoHeight: nextHeight
			});
		}
	}

	syncCropViewportSize() {
		const viewport = this.getCropViewportElement();
		if (!viewport) {
			return;
		}

		const computedStyle = window.getComputedStyle(viewport);
		const horizontalPadding =
			(parseFloat(computedStyle.paddingLeft) || 0) +
			(parseFloat(computedStyle.paddingRight) || 0);
		const verticalPadding =
			(parseFloat(computedStyle.paddingTop) || 0) +
			(parseFloat(computedStyle.paddingBottom) || 0);
		const nextWidth = Math.round(Math.max((viewport.clientWidth || 0) - horizontalPadding, 0));
		const nextHeight = Math.round(Math.max((viewport.clientHeight || 0) - verticalPadding, 0));
		if (!nextWidth || !nextHeight) {
			return;
		}

		if (
			nextWidth !== this.state.cropViewportWidth ||
			nextHeight !== this.state.cropViewportHeight
		) {
			this.setState({
				cropViewportWidth: nextWidth,
				cropViewportHeight: nextHeight
			});
		}
	}

	handleWindowResize() {
		window.requestAnimationFrame(() => {
			this.syncRenderedVideoHeight();
			this.syncCropViewportSize();
		});
	}

	formatTime(seconds = 0) {
		const value = Number.isFinite(seconds) ? seconds : 0;
		const whole = Math.max(Math.floor(value), 0);
		const mins = Math.floor(whole / 60);
		const secs = whole % 60;
		return `${mins}:${secs.toString().padStart(2, "0")}`;
	}

	handleChangeImageName(event) {
		this.setState({
			imageName: event && event.target ? event.target.value : ""
		});
	}

	handleToggleAutoLinkImage(event) {
		this.setState({
			autoLinkImage: !!(event && event.target ? event.target.checked : false)
		});
	}

	async waitForSeek(targetTime) {
		const video = this.getVideoElement();
		if (!video) {
			throw new Error("Video preview is not available yet.");
		}

		const nextTime = clampTime(targetTime, Number(video.duration) || this.state.duration || 0);
		if (Math.abs((video.currentTime || 0) - nextTime) < 0.05) {
			this.setState({
				currentTime: nextTime,
				seeking: false
			});
			return nextTime;
		}

		this.setState({
			seeking: true,
			currentTime: nextTime,
			error: ""
		});

		await new Promise((resolve, reject) => {
			let done = false;
			const cleanup = () => {
				video.removeEventListener("seeked", handleSeeked);
				video.removeEventListener("error", handleError);
				window.clearTimeout(timeoutId);
			};
			const finish = (callback) => {
				if (done) {
					return;
				}
				done = true;
				cleanup();
				callback();
			};
			const handleSeeked = () => finish(resolve);
			const handleError = () => finish(() => reject(new Error("Unable to seek video preview.")));
			const timeoutId = window.setTimeout(() => finish(resolve), 3500);

			video.addEventListener("seeked", handleSeeked);
			video.addEventListener("error", handleError);
			try {
				video.currentTime = nextTime;
			} catch (err) {
				finish(() => reject(err));
			}
		});

		const actualTime = Number(video.currentTime) || nextTime;
		this.setState({
			currentTime: actualTime,
			seeking: false
		});

		return actualTime;
	}

	drawCurrentFrame() {
		const video = this.getVideoElement();
		const canvas = this.getCanvasElement();
		if (!video || !canvas) {
			throw new Error("Video thumbnail preview is not ready.");
		}
		if (!video.videoWidth || !video.videoHeight) {
			throw new Error("Video has not loaded enough data yet.");
		}

		canvas.width = video.videoWidth;
		canvas.height = video.videoHeight;
		const context = canvas.getContext("2d");
		context.drawImage(video, 0, 0, canvas.width, canvas.height);
		return canvas;
	}

	async updatePreviewFrame() {
		try {
			const canvas = this.drawCurrentFrame();
			const previewDataUrl = canvas.toDataURL("image/png");
			this.setState({
				previewDataUrl,
				capturedImageWidth: canvas.width,
				capturedImageHeight: canvas.height,
				cropZoom: DEFAULT_CROP_ZOOM,
				cropOffsetX: 0,
				cropOffsetY: 0,
				freeCropWidthPct: 1,
				freeCropHeightPct: 1,
				error: ""
			}, () => {
				window.requestAnimationFrame(() => {
					this.syncCropViewportSize();
				});
			});
			return previewDataUrl;
		} catch (err) {
			const message = err && err.message ? err.message : `${err}`;
			this.setState({
				previewDataUrl: "",
				error: `Preview capture failed: ${message}`
			});
			throw err;
		}
	}

	async seekAndRefresh(targetTime) {
		await this.waitForSeek(targetTime);
		return this.updatePreviewFrame();
	}

	async handleVideoLoadedMetadata() {
		const video = this.getVideoElement();
		if (!video) {
			return;
		}

		const duration = Number.isFinite(video.duration) ? video.duration : 0;
		const initialTime = clampTime(INITIAL_FRAME_TIME_SECONDS, duration);
		this.setState({
			duration,
			videoReady: true,
			currentTime: initialTime,
			previewVideoLoading: false
		});
		window.requestAnimationFrame(() => {
			this.syncRenderedVideoHeight();
			window.requestAnimationFrame(() => {
				this.syncRenderedVideoHeight();
			});
		});

		try {
			await this.seekAndRefresh(initialTime);
		} catch (err) {
			console.warn(err);
		}
	}

	handleVideoError() {
		if (this.state.previewVideoUrl && this.state.previewVideoUrl !== this.props.videoUrl) {
			this.revokePreviewObjectUrl();
			this.setState({
				previewVideoUrl: this.props.videoUrl || "",
				previewVideoLoading: false,
				videoReady: false
			});
			return;
		}

		this.setState({
			previewVideoLoading: false,
			error: "The video preview could not be loaded."
		});
	}

	handleVideoSeeked() {
		const video = this.getVideoElement();
		const currentTime = video ? Number(video.currentTime) || 0 : this.state.currentTime;
		this.setState({
			currentTime
		});

		if (!this.state.seeking) {
			this.handleRefreshPreview();
		}
	}

	handleVideoPause() {
		const video = this.getVideoElement();
		const currentTime = video ? Number(video.currentTime) || 0 : this.state.currentTime;
		this.setState({
			currentTime
		}, () => {
			this.handleRefreshPreview();
		});
	}

	async handleRefreshPreview() {
		try {
			await this.updatePreviewFrame();
		} catch (err) {
			console.warn(err);
		}
	}

	isUsableFrame(canvas) {
		const width = Math.max(canvas.width, 1);
		const height = Math.max(canvas.height, 1);
		const sampleWidth = Math.min(width, 64);
		const sampleHeight = Math.min(height, 64);
		const sampleCanvas = document.createElement("canvas");
		sampleCanvas.width = sampleWidth;
		sampleCanvas.height = sampleHeight;
		const sampleContext = sampleCanvas.getContext("2d");
		sampleContext.drawImage(canvas, 0, 0, sampleWidth, sampleHeight);
		const imageData = sampleContext.getImageData(0, 0, sampleWidth, sampleHeight);
		let brightnessTotal = 0;
		let nonBlackPixels = 0;
		const totalPixels = Math.max(imageData.data.length / 4, 1);

		for (let index = 0; index < imageData.data.length; index += 4) {
			const brightness = (imageData.data[index] + imageData.data[index + 1] + imageData.data[index + 2]) / 3;
			brightnessTotal += brightness;
			if (brightness > MIN_NON_BLACK_AVERAGE) {
				nonBlackPixels += 1;
			}
		}

		const averageBrightness = brightnessTotal / totalPixels;
		const nonBlackPixelRatio = nonBlackPixels / totalPixels;
		return averageBrightness > MIN_NON_BLACK_AVERAGE && nonBlackPixelRatio > MIN_NON_BLACK_PIXEL_RATIO;
	}

	async handleFindFirstGoodFrame() {
		const video = this.getVideoElement();
		if (!video) {
			this.setState({
				error: "Video preview is not available yet."
			});
			return;
		}

		const duration = Number.isFinite(video.duration) ? video.duration : this.state.duration;
		if (!duration) {
			this.setState({
				error: "Load the video preview first."
			});
			return;
		}

		const nonce = this.openNonce;
		this.setState({
			findingFrame: true,
			error: ""
		});

		try {
			const limit = Math.min(duration, FRAME_SCAN_MAX_SECONDS);
			for (let targetTime = 0; targetTime <= limit; targetTime += FRAME_SCAN_STEP_SECONDS) {
				if (nonce !== this.openNonce) {
					return;
				}

				await this.waitForSeek(targetTime);
				const canvas = this.drawCurrentFrame();
				if (this.isUsableFrame(canvas)) {
					const previewDataUrl = canvas.toDataURL("image/png");
					this.setState({
						currentTime: targetTime,
						previewDataUrl,
						capturedImageWidth: canvas.width,
						capturedImageHeight: canvas.height,
						cropZoom: DEFAULT_CROP_ZOOM,
						cropOffsetX: 0,
						cropOffsetY: 0,
						freeCropWidthPct: 1,
						freeCropHeightPct: 1,
						error: ""
					});
					return;
				}
			}

			this.setState({
				error: "No clearly usable frame was found automatically. Scrub manually to choose one."
			});
		} catch (err) {
			const message = err && err.message ? err.message : `${err}`;
			this.setState({
				error: `Automatic frame scan failed: ${message}`
			});
		} finally {
			if (nonce === this.openNonce) {
				this.setState({
					findingFrame: false
				});
			}
		}
	}

	canvasToBlob(canvas) {
		return new Promise((resolve, reject) => {
			canvas.toBlob((blob) => {
				if (blob) {
					resolve(blob);
				} else {
					reject(new Error("Unable to create thumbnail image blob."));
				}
			}, "image/png");
		});
	}

	getAspectRatioForPreset(cropPreset = this.state.cropPreset) {
		const preset = CROP_PRESET_OPTIONS.find((option) => option.value === cropPreset);
		return preset ? preset.aspectRatio : null;
	}

	getCropMetrics(overrides = {}) {
		const {
			capturedImageWidth = this.state.capturedImageWidth,
			capturedImageHeight = this.state.capturedImageHeight,
			cropViewportWidth = this.state.cropViewportWidth,
			cropViewportHeight = this.state.cropViewportHeight,
			cropPreset = this.state.cropPreset,
			cropZoom = this.state.cropZoom,
			cropOffsetX = this.state.cropOffsetX,
			cropOffsetY = this.state.cropOffsetY,
			freeCropWidthPct = this.state.freeCropWidthPct,
			freeCropHeightPct = this.state.freeCropHeightPct
		} = overrides;

		if (!capturedImageWidth || !capturedImageHeight || !cropViewportWidth || !cropViewportHeight) {
			return null;
		}

		const aspectRatio = this.getAspectRatioForPreset(cropPreset);
		let viewportWidth = cropViewportWidth;
		let viewportHeight = cropViewportHeight;

		if (aspectRatio) {
			if (cropViewportWidth / cropViewportHeight > aspectRatio) {
				viewportHeight = cropViewportHeight;
				viewportWidth = viewportHeight * aspectRatio;
			} else {
				viewportWidth = cropViewportWidth;
				viewportHeight = viewportWidth / aspectRatio;
			}
		} else {
			const imageAspectRatio = capturedImageWidth / capturedImageHeight;
			let fitWidth = cropViewportWidth;
			let fitHeight = cropViewportHeight;
			if (cropViewportWidth / cropViewportHeight > imageAspectRatio) {
				fitHeight = cropViewportHeight;
				fitWidth = fitHeight * imageAspectRatio;
			} else {
				fitWidth = cropViewportWidth;
				fitHeight = fitWidth / imageAspectRatio;
			}

			viewportWidth = fitWidth * clampValue(freeCropWidthPct, MIN_FREE_CROP_SCALE, 1);
			viewportHeight = fitHeight * clampValue(freeCropHeightPct, MIN_FREE_CROP_SCALE, 1);
		}

		const zoom = clampValue(cropZoom, DEFAULT_CROP_ZOOM, MAX_CROP_ZOOM);
		const scale = Math.max(
			viewportWidth / capturedImageWidth,
			viewportHeight / capturedImageHeight
		) * zoom;
		const displayWidth = capturedImageWidth * scale;
		const displayHeight = capturedImageHeight * scale;
		const maxOffsetX = Math.max((displayWidth - viewportWidth) / 2, 0);
		const maxOffsetY = Math.max((displayHeight - viewportHeight) / 2, 0);
		const clampedOffsetX = clampValue(cropOffsetX, -maxOffsetX, maxOffsetX);
		const clampedOffsetY = clampValue(cropOffsetY, -maxOffsetY, maxOffsetY);

		return {
			viewportWidth,
			viewportHeight,
			displayWidth,
			displayHeight,
			scale,
			imageLeft: (viewportWidth - displayWidth) / 2 + clampedOffsetX,
			imageTop: (viewportHeight - displayHeight) / 2 + clampedOffsetY,
			clampedOffsetX,
			clampedOffsetY,
			capturedImageWidth,
			capturedImageHeight
		};
	}

	handleChangeCropPreset(cropPreset) {
		this.setState({
			cropPreset,
			cropZoom: DEFAULT_CROP_ZOOM,
			cropOffsetX: 0,
			cropOffsetY: 0,
			freeCropWidthPct: 1,
			freeCropHeightPct: 1
		});
	}

	handleCropZoomChange(event) {
		const cropZoom = clampValue(parseFloat(event && event.target ? event.target.value : DEFAULT_CROP_ZOOM) || DEFAULT_CROP_ZOOM, DEFAULT_CROP_ZOOM, MAX_CROP_ZOOM);
		const nextMetrics = this.getCropMetrics({
			cropZoom
		});

		this.setState({
			cropZoom,
			cropOffsetX: nextMetrics ? nextMetrics.clampedOffsetX : 0,
			cropOffsetY: nextMetrics ? nextMetrics.clampedOffsetY : 0
		});
	}

	handleFreeCropWidthChange(event) {
		const freeCropWidthPct = clampValue(parseFloat(event && event.target ? event.target.value : 1) || 1, MIN_FREE_CROP_SCALE, 1);
		const nextMetrics = this.getCropMetrics({
			freeCropWidthPct
		});

		this.setState({
			freeCropWidthPct,
			cropOffsetX: nextMetrics ? nextMetrics.clampedOffsetX : 0,
			cropOffsetY: nextMetrics ? nextMetrics.clampedOffsetY : 0
		});
	}

	handleFreeCropHeightChange(event) {
		const freeCropHeightPct = clampValue(parseFloat(event && event.target ? event.target.value : 1) || 1, MIN_FREE_CROP_SCALE, 1);
		const nextMetrics = this.getCropMetrics({
			freeCropHeightPct
		});

		this.setState({
			freeCropHeightPct,
			cropOffsetX: nextMetrics ? nextMetrics.clampedOffsetX : 0,
			cropOffsetY: nextMetrics ? nextMetrics.clampedOffsetY : 0
		});
	}

	handleResetCrop() {
		this.setState({
			cropZoom: DEFAULT_CROP_ZOOM,
			cropOffsetX: 0,
			cropOffsetY: 0,
			freeCropWidthPct: 1,
			freeCropHeightPct: 1
		});
	}

	handleCropDragStart(event) {
		if (!this.state.previewDataUrl) {
			return;
		}
		if (event && typeof event.button === "number" && event.button !== 0) {
			return;
		}

		const metrics = this.getCropMetrics();
		if (!metrics) {
			return;
		}

		event.preventDefault();
		this.dragState = {
			startX: event.clientX,
			startY: event.clientY,
			originOffsetX: metrics.clampedOffsetX,
			originOffsetY: metrics.clampedOffsetY
		};

		window.addEventListener("mousemove", this.handleWindowMouseMove);
		window.addEventListener("mouseup", this.handleWindowMouseUp);
		this.setState({
			isDraggingCrop: true
		});
	}

	handleWindowMouseMove(event) {
		if (!this.dragState) {
			return;
		}

		const nextMetrics = this.getCropMetrics({
			cropOffsetX: this.dragState.originOffsetX + (event.clientX - this.dragState.startX),
			cropOffsetY: this.dragState.originOffsetY + (event.clientY - this.dragState.startY)
		});
		if (!nextMetrics) {
			return;
		}

		this.setState({
			cropOffsetX: nextMetrics.clampedOffsetX,
			cropOffsetY: nextMetrics.clampedOffsetY
		});
	}

	handleWindowMouseUp() {
		this.detachCropDragListeners();
	}

	detachCropDragListeners() {
		window.removeEventListener("mousemove", this.handleWindowMouseMove);
		window.removeEventListener("mouseup", this.handleWindowMouseUp);
		this.dragState = null;
		if (this.state.isDraggingCrop) {
			this.setState({
				isDraggingCrop: false
			});
		}
	}

	createOutputCanvas(sourceCanvas) {
		const metrics = this.getCropMetrics();
		if (!metrics) {
			return sourceCanvas;
		}

		const sourceX = clampValue((-metrics.imageLeft) / metrics.scale, 0, metrics.capturedImageWidth - 1);
		const sourceY = clampValue((-metrics.imageTop) / metrics.scale, 0, metrics.capturedImageHeight - 1);
		const sourceWidth = clampValue(metrics.viewportWidth / metrics.scale, 1, metrics.capturedImageWidth - sourceX);
		const sourceHeight = clampValue(metrics.viewportHeight / metrics.scale, 1, metrics.capturedImageHeight - sourceY);

		const outputCanvas = document.createElement("canvas");
		outputCanvas.width = Math.max(1, Math.round(sourceWidth));
		outputCanvas.height = Math.max(1, Math.round(sourceHeight));
		const outputContext = outputCanvas.getContext("2d");
		outputContext.drawImage(
			sourceCanvas,
			sourceX,
			sourceY,
			sourceWidth,
			sourceHeight,
			0,
			0,
			outputCanvas.width,
			outputCanvas.height
		);
		return outputCanvas;
	}

	async handleCreateThumbnail() {
		const imageName = `${this.state.imageName || ""}`.trim();
		if (!imageName) {
			this.setState({
				error: "Specify an image name before creating the thumbnail."
			});
			return;
		}

		this.setState({
			capturing: true,
			error: ""
		});

		try {
			const canvas = this.drawCurrentFrame();
			const outputCanvas = this.createOutputCanvas(canvas);
			const blob = await this.canvasToBlob(outputCanvas);
			if (this.props.onCreate) {
				this.props.onCreate({
					blob,
					imageName: imageName.toLowerCase().endsWith(".png") ? imageName : `${imageName}.png`,
					currentTime: this.state.currentTime,
					autoLink: this.state.autoLinkImage
				});
			}
		} catch (err) {
			const message = err && err.message ? err.message : `${err}`;
			this.setState({
				error: `Thumbnail capture failed: ${message}`
			});
		} finally {
			this.setState({
				capturing: false
			});
		}
	}

	render() {
		const {
			open,
			onClose,
			videoLabel,
			autoLinkLabel
		} = this.props;
		const {
			autoLinkImage,
			capturing,
			currentTime,
			cropPreset,
			cropZoom,
			duration,
			error,
			freeCropHeightPct,
			freeCropWidthPct,
			findingFrame,
			imageName,
			isDraggingCrop,
			previewDataUrl,
			previewVideoLoading,
			previewVideoUrl,
			renderedVideoHeight,
			seeking,
			videoReady
		} = this.state;

		if (!open) {
			return null;
		}

		const compactLayout = typeof window !== "undefined" && window.innerWidth < 1180;
		const viewportHeight = typeof window !== "undefined" ? window.innerHeight || 900 : 900;
		const overlayStyle = {
			position: "fixed",
			inset: 0,
			backgroundColor: "rgba(15, 23, 42, 0.45)",
			zIndex: 200000,
			display: "flex",
			alignItems: "stretch",
			justifyContent: "stretch",
			padding: "12px",
			boxSizing: "border-box"
		};
		const dialogStyle = {
			flex: "1 1 auto",
			minWidth: 0,
			minHeight: 0,
			backgroundColor: "#ffffff",
			borderRadius: "24px",
			boxSizing: "border-box",
			display: "flex",
			flexDirection: "column",
			overflow: "hidden",
			maxHeight: "calc(100vh - 24px)",
			boxShadow: "0 20px 60px rgba(15, 23, 42, 0.28)"
		};
		const headerStyle = {
			padding: "22px 28px 10px 28px",
			borderBottom: "1px solid #e5e7eb",
			display: "flex",
			alignItems: "flex-start",
			justifyContent: "space-between",
			gap: "16px"
		};
		const bodyStyle = {
			flex: "1 1 auto",
			minHeight: 0,
			overflow: "auto",
			padding: "18px 28px 12px 28px",
			display: "flex",
			flexDirection: "column",
			gap: "14px"
		};
		const footerStyle = {
			borderTop: "1px solid #e5e7eb",
			padding: "14px 28px 18px 28px",
			display: "flex",
			justifyContent: "space-between",
			gap: "12px",
			flexWrap: "wrap",
			backgroundColor: "#ffffff"
		};
		const availableMediaHeight = Math.max(viewportHeight - (compactLayout ? 600 : 520), compactLayout ? 210 : 260);
		const fallbackVideoPaneHeight = Math.min(compactLayout ? 270 : 360, availableMediaHeight);
		const measuredVideoPaneHeight = renderedVideoHeight || fallbackVideoPaneHeight;
		const previewPaneHeight = Math.min(measuredVideoPaneHeight, fallbackVideoPaneHeight, availableMediaHeight);
		const cropMetrics = this.getCropMetrics();
		const canEditCrop = !!(previewDataUrl && cropMetrics);

		return (
			<div style={overlayStyle} onMouseDown={onClose}>
				<div style={dialogStyle} onMouseDown={(event) => event.stopPropagation()}>
					<div style={headerStyle}>
						<div style={{ minWidth: 0, flex: 1 }}>
							<h3 style={{ margin: 0, textAlign: "center" }}>Create Video Thumbnail</h3>
							{videoLabel && <div style={{ marginTop: "10px", textAlign: "center", color: "#666666" }}>{videoLabel}</div>}
						</div>
						<Button bsStyle="link" onClick={onClose} style={{ color: "#111827", padding: 0, fontSize: "28px", lineHeight: 1 }}>
							<FontAwesomeIcon icon="times" />
						</Button>
					</div>
					<div style={bodyStyle}>
						<div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
							<label style={{ color: "gray" }}>Image name</label>
							<input className="form-control" type="text" value={imageName} onChange={this.handleChangeImageName} placeholder="video-thumbnail.png" />
						</div>
						<label style={{ display: "flex", alignItems: "center", gap: "10px", margin: 0, color: "#374151", fontWeight: 500 }}>
							<input type="checkbox" checked={autoLinkImage} onChange={this.handleToggleAutoLinkImage} />
							<span>{autoLinkLabel || "Auto-link Image"}</span>
						</label>
						<div style={{ color: "#666666", fontSize: "12px", lineHeight: 1.4, marginTop: "-6px" }}>
							When off, the thumbnail is still saved to Drive but it will not update the item&apos;s image field.
						</div>
						<div style={{ display: "grid", gridTemplateColumns: compactLayout ? "minmax(0, 1fr)" : "minmax(0, 1.65fr) minmax(340px, 1fr)", gap: "20px", alignItems: "start" }}>
							<div style={{ display: "flex", flexDirection: "column", gap: "8px", minWidth: 0 }}>
								<div style={{ color: "gray" }}>Video preview</div>
								<div style={{ position: "relative", borderRadius: "12px", overflow: "hidden", background: "#111111", height: `${previewPaneHeight}px`, minHeight: `${previewPaneHeight}px`, display: "flex", alignItems: "center", justifyContent: "center" }}>
									<video
										key={previewVideoUrl || "video-thumbnail-preview"}
										ref={this.videoRef}
										src={previewVideoUrl}
										preload="auto"
										controls
										muted
										playsInline
										onLoadedMetadata={this.handleVideoLoadedMetadata}
										onLoadedData={this.syncRenderedVideoHeight}
										onCanPlay={this.syncRenderedVideoHeight}
										onSeeked={this.handleVideoSeeked}
										onPause={this.handleVideoPause}
										onError={this.handleVideoError}
										style={{ width: "100%", maxHeight: `${previewPaneHeight}px`, background: "black", borderRadius: "8px" }}
									/>
									{previewVideoLoading && (
										<div style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", background: "rgba(17, 17, 17, 0.35)", color: "#ffffff", fontWeight: 600 }}>
											<FontAwesomeIcon icon="spinner" spin style={{ marginRight: "8px" }} /> Loading video...
										</div>
									)}
								</div>
								<div style={{ color: "#666666", fontSize: "12px", lineHeight: 1.4 }}>
									Use the video&apos;s built-in scrubber, then release or pause to update the thumbnail frame. Current frame: {this.formatTime(currentTime)} / {this.formatTime(duration)}
								</div>
								<div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
									<div style={{ display: "flex", gap: "10px", flexWrap: "wrap" }}>
										<Button onClick={this.handleRefreshPreview} disabled={!videoReady || seeking || capturing || previewVideoLoading}>
											<FontAwesomeIcon icon="image" /> Refresh Preview
										</Button>
										<Button onClick={this.handleFindFirstGoodFrame} disabled={!videoReady || seeking || capturing || previewVideoLoading || findingFrame}>
											<FontAwesomeIcon icon={findingFrame ? "spinner" : "magic"} spin={findingFrame} /> Find First Usable Frame
										</Button>
									</div>
									<div style={{ color: "#666666", fontSize: "12px", lineHeight: 1.4 }}>
										Auto-find skips mostly black opening frames. Use the built-in scrubber when you want a precise frame.
									</div>
								</div>
							</div>
							<div style={{ display: "flex", flexDirection: "column", gap: "8px", minWidth: 0 }}>
								<div style={{ color: "gray" }}>Thumbnail preview</div>
								<div
									ref={this.cropViewportRef}
									style={{
										border: "1px solid #dddddd",
										borderRadius: "12px",
										height: `${previewPaneHeight}px`,
										maxHeight: `${previewPaneHeight}px`,
										display: "flex",
										alignItems: "center",
										justifyContent: "center",
										background: "#f8fafc",
										overflow: "hidden",
										padding: "12px"
									}}
								>
									{previewDataUrl ? (
										<div
											onMouseDown={this.handleCropDragStart}
											style={{
												width: cropMetrics ? `${cropMetrics.viewportWidth}px` : "100%",
												height: cropMetrics ? `${cropMetrics.viewportHeight}px` : "100%",
												maxWidth: "100%",
												maxHeight: "100%",
												position: "relative",
												overflow: "hidden",
												borderRadius: "10px",
												background: "#111111",
												boxShadow: "inset 0 0 0 1px rgba(255, 255, 255, 0.14)",
												cursor: isDraggingCrop ? "grabbing" : "grab"
											}}
										>
											{cropMetrics && (
												<img
													alt="Thumbnail preview"
													src={previewDataUrl}
													draggable={false}
													onDragStart={(event) => event.preventDefault()}
													style={{
														position: "absolute",
														left: `${cropMetrics.imageLeft}px`,
														top: `${cropMetrics.imageTop}px`,
														width: `${cropMetrics.displayWidth}px`,
														height: `${cropMetrics.displayHeight}px`,
														maxWidth: "none",
														userSelect: "none",
														pointerEvents: "none"
													}}
												/>
											)}
										</div>
									) : (
										<div style={{ padding: "20px", color: "#777777", textAlign: "center" }}>
											Use the scrubber or playback controls, then refresh the preview.
										</div>
									)}
								</div>
								<div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
									<div style={{ color: "#666666", fontSize: "12px", textTransform: "uppercase", letterSpacing: "0.04em", fontWeight: 600 }}>
										Crop preset
									</div>
									<div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
										{CROP_PRESET_OPTIONS.map((option) => (
											<Button
												key={option.value}
												bsSize="small"
												bsStyle={cropPreset === option.value ? "primary" : "default"}
												active={cropPreset === option.value}
												onClick={() => this.handleChangeCropPreset(option.value)}
												disabled={!previewDataUrl}
											>
												{option.label}
											</Button>
										))}
									</div>
									<div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
										<div style={{ minWidth: "68px", color: "#666666", fontSize: "12px", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.04em" }}>
											Zoom
										</div>
										<input
											type="range"
											min={DEFAULT_CROP_ZOOM}
											max={MAX_CROP_ZOOM}
											step={0.05}
											value={cropZoom}
											onInput={this.handleCropZoomChange}
											onChange={this.handleCropZoomChange}
											disabled={!previewDataUrl}
											style={{ flex: 1 }}
										/>
										<div style={{ minWidth: "44px", textAlign: "right", color: "#666666" }}>
											{cropZoom.toFixed(2)}x
										</div>
									</div>
									{cropPreset === FREE_CROP_PRESET && (
										<div style={{ display: "grid", gridTemplateColumns: compactLayout ? "minmax(0, 1fr)" : "minmax(0, 1fr) minmax(0, 1fr)", gap: "10px" }}>
											<div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
												<div style={{ minWidth: "68px", color: "#666666", fontSize: "12px", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.04em" }}>
													Width
												</div>
												<input
													type="range"
													min={MIN_FREE_CROP_SCALE}
													max={1}
													step={0.01}
													value={freeCropWidthPct}
													onInput={this.handleFreeCropWidthChange}
													onChange={this.handleFreeCropWidthChange}
													disabled={!previewDataUrl}
													style={{ flex: 1 }}
												/>
											</div>
											<div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
												<div style={{ minWidth: "68px", color: "#666666", fontSize: "12px", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.04em" }}>
													Height
												</div>
												<input
													type="range"
													min={MIN_FREE_CROP_SCALE}
													max={1}
													step={0.01}
													value={freeCropHeightPct}
													onInput={this.handleFreeCropHeightChange}
													onChange={this.handleFreeCropHeightChange}
													disabled={!previewDataUrl}
													style={{ flex: 1 }}
												/>
											</div>
										</div>
									)}
									<div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: "10px", flexWrap: "wrap" }}>
										<div style={{ color: "#666666", fontSize: "12px", lineHeight: 1.4 }}>
											Drag inside the preview to reframe. Only the visible crop saves.
										</div>
										<Button bsSize="small" onClick={this.handleResetCrop} disabled={!canEditCrop}>
											Reset Crop
										</Button>
									</div>
								</div>
								{error && (
									<div style={{ color: "#b94a48", fontSize: "13px" }}>
										{error}
									</div>
								)}
							</div>
						</div>
						<canvas ref={this.canvasRef} style={{ display: "none" }} />
					</div>
					<div style={footerStyle}>
						<Button onClick={onClose}>
							<FontAwesomeIcon icon="ban" /> Cancel
						</Button>
						<Button bsStyle="primary" onClick={this.handleCreateThumbnail} disabled={!videoReady || seeking || capturing || previewVideoLoading || !previewDataUrl}>
							<FontAwesomeIcon icon={capturing ? "spinner" : "camera"} spin={capturing} /> {capturing ? "Creating..." : "Create Thumbnail"}
						</Button>
					</div>
				</div>
			</div>
		);
	}
}
